import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Pagination,
  ApiVisa, CashbookContainerModel, CashbookService } from 'blg-akaun-ts-lib';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { AppConfig } from '../../shared-utilities/visa';

@Component({
  selector: 'blg-select-cashbook-drop-down',
  templateUrl: './select-cashbook-drop-down.component.html',
  styleUrls: ['./select-cashbook-drop-down.component.scss']
})

export class SelectCashbookDropDownComponent implements OnInit, OnDestroy {

  apiVisa = AppConfig.apiVisa;
  @Input() cashbook = new FormControl();
  @Input() required: boolean;

  @Output() cashbookChange = new EventEmitter<FormControl>();
  @Output() cashbookSelected = new EventEmitter<CashbookContainerModel>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<CashbookContainerModel[]> = new ReplaySubject<CashbookContainerModel[]>(1);
  cashbookList: CashbookContainerModel[] = [];
  selectedCashbook: CashbookContainerModel;
  filteredCashbook: any = [];

  protected _onDestroy = new Subject<void>();

  constructor(
    private cashbookService: CashbookService
  ) {
  }

  ngOnInit() {
    if (this.cashbook.value) {
      this.cashbookService.getByGuid(this.cashbook.value, this.apiVisa).subscribe( resolved => {
        this.cashbookList = [resolved.data];
        this.filteredOptions$.next([resolved.data]);
        this.onSelect(resolved.data.bl_fi_mst_cashbook_hdr.guid.toString());
      });
    }
    this.serverSideFiltering.valueChanges.pipe(
      filter(searchText => searchText.length > 3),
      tap(() => this.serverSideSearching = true),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchText => this.searchCashbook(searchText))
    ).subscribe( resolved => {
      this.serverSideSearching = false;

      if (this.selectedCashbook) {
        this.filteredOptions$.next([...resolved, this.selectedCashbook]);
        this.cashbookList = [...resolved, this.selectedCashbook];
      } else {
        this.filteredOptions$.next(resolved);
        this.cashbookList = resolved;
      }
    }, error => {
      this.serverSideSearching = false;
      console.error(error);
    });
  }

  /**
   * Server-side cashbook typeahead.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_fi_mst_cashbook_hdr hdr
   *   WHERE hdr.status != 'DELETED'
   *     AND (hdr.code ILIKE '%x%' OR hdr.name ILIKE '%x%')
   *   ORDER BY hdr.name, hdr.guid
   *
   * GET erp/cashbooks/query -> CashbookUow.getByCriteria ->
   * generateWhereClauseByCriteriaUpdated applies `search_word` (bound, ILIKE over
   * code, name and acc_no) and defaults to `status != 'DELETED'`.
   *
   * Behaviour notes:
   * - `search_word` also matches acc_no, so the typeahead is slightly broader.
   * - One request now instead of one subquery plus one call per hit. The old
   *   per-hit call passed `hdr_guids`, which CashbookQueryCriteria does not define
   *   (its guid filter is `guids`), so that criterion was ignored.
   */
  searchCashbook(searchText: string) {
    return this.cashbookService.getByCriteria(
      new Pagination(0, 100, [
        { columnName: 'search_word', operator: '=', value: (searchText || '').trim() }
      ]),
      this.apiVisa
    ).pipe(
      map(response => response.data)
    );
  }

  onSelect(cashbookGuid: string) {
    if (cashbookGuid) {
      this.cashbook.patchValue(cashbookGuid);
      this.selectedCashbook = this.cashbookList.find(cashbook => cashbook.bl_fi_mst_cashbook_hdr.guid === cashbookGuid);
      this.cashbookSelected.emit(this.selectedCashbook);
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
